import React, { useState } from "react";
import { Input, Select, DatePicker, Button } from "antd";
import { FilterOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

interface FilterField {
  key: string;
  label: string;
  type: "text" | "select" | "date" | "status";
  options?: { value: string | number; label: string }[];
}

interface LocationFiltersProps {
  fields: FilterField[];
  onApplyFilters: (filters: Record<string, any>) => void;
  onClearFilters: () => void;
}

const LocationFilters: React.FC<LocationFiltersProps> = ({
  fields,
  onApplyFilters,
  onClearFilters,
}) => {
  const [filters, setFilters] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    // Handle empty values properly
    if (value === undefined || value === "") {
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleApplyFilters = () => {
    // Transform date range to start_date and end_date
    const transformedFilters = { ...filters };
    if (
      filters.date_range &&
      Array.isArray(filters.date_range) &&
      filters.date_range.length === 2
    ) {
      transformedFilters.start_date = filters.date_range[0];
      transformedFilters.end_date = filters.date_range[1];
      delete transformedFilters.date_range;
    }
    onApplyFilters(transformedFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
    onClearFilters();
  };

  return (
    <div className="bg-white p-4 shadow-md rounded-lg space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {fields.map(({ key, label, type, options }) => (
          <div key={key}>
            {type === "text" && (
              <Input
                placeholder={label}
                value={filters[key] || ""}
                onChange={(e) => handleFilterChange(key, e.target.value)}
                allowClear
              />
            )}
            {type === "select" && (
              <Select
                placeholder={label}
                value={filters[key]}
                onChange={(value) => handleFilterChange(key, value)}
                className="w-full"
                options={options}
                allowClear
              />
            )}
            {type === "date" && (
              <RangePicker
                className="w-full"
                placeholder={["Start Date", "End Date"]}
                value={filters[key]}
                onChange={(dates) =>
                  handleFilterChange(
                    key,
                    dates?.map((d) => d?.format("YYYY-MM-DD")) ?? undefined
                  )
                }
                allowClear
              />
            )}
            {type === "status" && (
              <Select
                placeholder="Status"
                value={filters[key]}
                className="w-full"
                onChange={(value) => handleFilterChange(key, value)}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                allowClear
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={handleApplyFilters}
        >
          Apply Filters
        </Button>
        <Button onClick={handleClearFilters}>Clear Filters</Button>
      </div>
    </div>
  );
};

export default LocationFilters;
